import type { HydratedAsset } from '../publicDto';
import { AssetMedia } from './AssetMedia';

// Ad Preview Mockups Stage 1 §4.4. A single Meta Story rendered as if
// it were a live in-feed ad.
//
// Container: 9:16 aspect, max ~360px wide on desktop (full-width on
// mobile). The PreviewPage renders one StoryMockup per Story-typed
// asset, stacked vertically per spec.
//
// Decorative chrome:
//   - top bar: brand profile circle + name + "Sponsored" + close icon
//   - bottom CTA button styled per Meta's actual ad CTAs (Learn More,
//     Sign Up, ...). The button is decorative — does NOT navigate.
//
// Mockup fidelity is "reasonable approximation" per spec §5.3, not
// pixel-perfect Meta UI.

type Props = {
  asset: HydratedAsset;
  // Brand identity — drives the BrandCircle's initial char only.
  // Falls back as the chrome page-name text if `pageName` isn't passed,
  // so older callers keep working.
  brandName: string;
  // Stage 1 §13.16. Simulated Meta page name in the top-bar chrome
  // (e.g. "Snap Fitness Barrington" composed from clubNamePrefix +
  // franchiseSuburb). Distinct from brandName because franchisees
  // recognise their own location, not just the brand. Optional for
  // tests; defaults to brandName when omitted.
  pageName?: string;
  ctaLabel: string | null;
  // Stage 1 §13.11. Optional caption rendered above the CTA button
  // — typically the per-asset headline or body excerpt picked by
  // pairAssetsWithText. Real Meta Story ads often overlay short
  // copy in the bottom third, so this matches the franchisee's
  // mental model when reviewing.
  caption?: string | null;
};

export function StoryMockup({
  asset,
  brandName,
  pageName,
  ctaLabel,
  caption,
}: Props) {
  const chromeName = pageName?.trim() || brandName;
  return (
    <div className="mx-auto max-w-[360px] w-full">
      <div className="relative rounded-xl overflow-hidden bg-black shadow-lg">
        {/* Asset slot — image or video, 9:16. */}
        <AssetMedia asset={asset} aspectClass="aspect-[9/16]" />

        {/* Top chrome — overlays the asset with a subtle gradient so
            the brand info stays readable against any creative. */}
        <div className="absolute inset-x-0 top-0 px-3 pt-3 pb-6 bg-gradient-to-b from-black/60 to-transparent">
          <div className="flex items-center gap-2 text-white text-sm">
            <BrandCircle brandName={brandName} />
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{chromeName}</p>
              <p className="text-xs text-white/80">Sponsored</p>
            </div>
            <button
              type="button"
              aria-hidden
              tabIndex={-1}
              className="text-white/80 text-lg leading-none cursor-default"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Bottom region — caption + CTA both share the gradient
            overlay so they read together against any creative. */}
        {(caption || ctaLabel) && (
          <div className="absolute inset-x-0 bottom-0 px-3 pb-4 pt-10 bg-gradient-to-t from-black/80 via-black/60 to-transparent space-y-2">
            {caption && (
              <p className="text-white text-sm leading-snug line-clamp-3 drop-shadow">
                {caption}
              </p>
            )}
            {ctaLabel && (
              <button
                type="button"
                aria-hidden
                tabIndex={-1}
                className="w-full rounded-lg bg-white/95 text-slate-900 font-semibold py-2.5 text-sm cursor-default hover:bg-white"
              >
                {ctaLabel}
              </button>
            )}
          </div>
        )}
      </div>
      {/* Per-mockup label so the AM can tell which Drive asset they're
          looking at. Read-only — useful when the franchisee wants to
          tell the agency "the second one looks wrong". */}
      {asset.label && (
        <p className="text-xs text-slate-500 text-center mt-2">
          {asset.label}
        </p>
      )}
    </div>
  );
}

// Tiny placeholder profile circle. Uses brand name's initial so each
// brand renders with a stable, recognisable mark even though we don't
// have a real brand logo wired into the schema yet.
function BrandCircle({ brandName }: { brandName: string }) {
  const initial = brandName.trim().charAt(0).toUpperCase() || '•';
  return (
    <div className="h-8 w-8 rounded-full bg-slate-300 text-slate-900 flex items-center justify-center text-sm font-semibold">
      {initial}
    </div>
  );
}
